import { getAuthToken, getMFAToken, getRefreshToken, useAuthStore } from '@/store/useAuthStore'
import type { LoginResponse } from '@/types/response'
import { endSession, isRevokedSession } from '@/lib/auth-session'

const BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:8000'

export type ApiAdapter = 'fetch' | 'xhr'

export type ApiRequestOptions = RequestInit & {
  params?: object | null
  adapter?: ApiAdapter
  onUploadProgress?: (progress: number) => void
}

export type BlobRequestOptions = RequestInit & {
  params?: object | null
  onDownloadProgress?: (progress: number) => void
}

class ApiError extends Error {
  status: number
  data: unknown

  constructor(status: number, data: unknown) {
    super(`API Error ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

let refreshTokenPromise: Promise<boolean> | null = null

const MFA_TOKEN_PATHS = ['/api/account/mfa-verify', '/api/account/mfa-recovery', '/api/account/mfa-setup']

function logRequestError(path: string, init: RequestInit, error: unknown) {
  if (init.signal?.aborted) return
  console.error(`[API] ${init.method ?? 'GET'} ${path} failed:`, error)
}

function buildUrl(path: string, params?: object | null) {
  const url = new URL(path, BASE_URL)
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value))
      }
    }
  }
  url.search = url.search.replaceAll('+', '%20')
  return url.toString()
}

async function refreshAccessToken(): Promise<boolean> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return false
  const generation = useAuthStore.getState().sessionGeneration

  refreshTokenPromise ??= fetch(buildUrl('/api/account/refresh-token'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
    .then(async (response) => {
      if (response.status >= 500) throw new ApiError(response.status, null)
      if (!response.ok) return false

      const data = (await response.json().catch(() => null)) as LoginResponse | null
      if (!data?.access_token || !data.refresh_token) return false
      if (useAuthStore.getState().sessionGeneration !== generation || getRefreshToken() !== refreshToken) return false

      useAuthStore.getState().setAuthTokens(data.access_token, data.refresh_token)
      return true
    })
    .finally(() => {
      refreshTokenPromise = null
    })

  return refreshTokenPromise
}

async function fetchWithAuth(path: string, init: RequestInit, params?: object | null) {
  const headers = getAuthHeaders(path, init)

  return fetch(buildUrl(path, params), { ...init, headers })
}

function getAuthHeaders(path: string, init: RequestInit) {
  const token = MFA_TOKEN_PATHS.includes(path) ? getMFAToken() : getAuthToken()
  const headers = new Headers(init.headers)
  if (!headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  return headers
}

function xhrWithAuth(
  path: string,
  init: RequestInit,
  params: object | null | undefined,
  onUploadProgress?: (progress: number) => void,
) {
  return new Promise<Response>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const headers = getAuthHeaders(path, init)

    xhr.open(init.method ?? 'GET', buildUrl(path, params), true)
    xhr.withCredentials = init.credentials === 'include'
    headers.forEach((value, key) => xhr.setRequestHeader(key, value))

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onUploadProgress?.((event.loaded / event.total) * 100)
    })

    xhr.addEventListener('load', () => {
      const responseHeaders = new Headers()
      for (const header of xhr
        .getAllResponseHeaders()
        .trim()
        .split(/[\r\n]+/)) {
        const separatorIndex = header.indexOf(':')
        if (separatorIndex > 0)
          responseHeaders.append(header.slice(0, separatorIndex), header.slice(separatorIndex + 1).trim())
      }
      resolve(
        new Response(xhr.responseText, { status: xhr.status, statusText: xhr.statusText, headers: responseHeaders }),
      )
    })
    xhr.addEventListener('error', () => reject(new TypeError('Network request failed')))
    xhr.addEventListener('abort', () => reject(new DOMException('The request was aborted.', 'AbortError')))

    if (init.signal) {
      if (init.signal.aborted) xhr.abort()
      init.signal.addEventListener('abort', () => xhr.abort(), { once: true })
    }

    xhr.send(init.body as XMLHttpRequestBodyInit | null)
  })
}

async function fetchBlobWithAuth(path: string, init: RequestInit, params?: object | null) {
  const token = getAuthToken()
  const headers = new Headers(init.headers)

  if (!headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  return fetch(buildUrl(path, params), { ...init, headers })
}

async function handleUnauthorized(
  path: string,
  retry: () => Promise<Response>,
  shouldRetry: boolean,
  response: Response,
  generation: number,
  authToken: string | null,
): Promise<Response> {
  const data: unknown = await response
    .clone()
    .json()
    .catch(() => null)
  if (useAuthStore.getState().sessionGeneration !== generation) throw new ApiError(401, data)
  if (path === '/api/account/login' || path.startsWith('/api/account/sso-login/')) {
    throw new ApiError(401, data)
  }
  if (isRevokedSession(data)) {
    endSession(true, authToken)
    throw new ApiError(401, data)
  }
  if (
    path === '/api/account/change-password' &&
    data &&
    typeof data === 'object' &&
    'message' in data &&
    data.message === 'Old password mismatch.'
  ) {
    throw new ApiError(401, data)
  }
  if (!shouldRetry || path === '/api/account/refresh-token' || !(await refreshAccessToken())) {
    if (useAuthStore.getState().sessionGeneration === generation) endSession(true, authToken)
    throw new ApiError(401, null)
  }

  const retryAuthToken = getAuthToken()
  const retryResponse = await retry()
  if (retryResponse.status === 401) {
    return handleUnauthorized(path, retry, false, retryResponse, generation, retryAuthToken)
  }

  return retryResponse
}

async function request<T>(path: string, options: ApiRequestOptions = {}, shouldRetry = true): Promise<T> {
  const generation = useAuthStore.getState().sessionGeneration
  const authToken = getAuthToken()
  const { adapter = 'fetch', onUploadProgress, params, ...init } = options
  const execute = () =>
    adapter === 'xhr' ? xhrWithAuth(path, init, params, onUploadProgress) : fetchWithAuth(path, init, params)
  let response: Response
  try {
    response = await execute()
  } catch (error) {
    logRequestError(path, init, error)
    throw error
  }

  if (response.status === 401) {
    response = await handleUnauthorized(path, execute, shouldRetry, response, generation, authToken)
  }

  if (useAuthStore.getState().sessionGeneration !== generation) throw new ApiError(401, null)

  if (!response.ok) {
    const data = await response.json().catch(() => null)
    throw new ApiError(response.status, data)
  }

  if (response.status === 204) return undefined as T
  return response.json()
}

async function requestBlob(
  path: string,
  options: BlobRequestOptions = {},
  shouldRetry = true,
): Promise<{ blob: Blob; filename: string }> {
  const generation = useAuthStore.getState().sessionGeneration
  const authToken = getAuthToken()
  const { params, onDownloadProgress, ...init } = options
  let response: Response
  try {
    response = await fetchBlobWithAuth(path, init, params)
  } catch (error) {
    logRequestError(path, init, error)
    throw error
  }

  if (response.status === 401) {
    response = await handleUnauthorized(
      path,
      () => fetchBlobWithAuth(path, init, params),
      shouldRetry,
      response,
      generation,
      authToken,
    )
  }

  if (useAuthStore.getState().sessionGeneration !== generation) throw new ApiError(401, null)

  if (!response.ok) {
    const data = await response.json().catch(() => null)
    throw new ApiError(response.status, data)
  }

  const disposition = response.headers.get('Content-Disposition') || ''
  const match = disposition.match(/filename="?(.+?)"?$/)
  const filename = match?.[1] ?? 'download'
  if (!onDownloadProgress || !response.body) {
    const blob = await response.blob()
    return { blob, filename }
  }

  const reader = response.body.getReader()
  const chunks: Uint8Array<ArrayBuffer>[] = []
  const total = Number(response.headers.get('Content-Length'))
  let received = 0
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      chunks.push(value)
      received += value.byteLength
      if (total > 0) onDownloadProgress(Math.min(100, (received / total) * 100))
    }
    init.signal?.throwIfAborted()
    if (useAuthStore.getState().sessionGeneration !== generation) throw new ApiError(401, null)
    onDownloadProgress(100)
    return { blob: new Blob(chunks, { type: response.headers.get('Content-Type') || '' }), filename }
  } finally {
    reader.releaseLock()
  }
}

async function requestStream(
  path: string,
  options: RequestInit & { params?: object | null } = {},
  shouldRetry = true,
): Promise<Response> {
  const generation = useAuthStore.getState().sessionGeneration
  const authToken = getAuthToken()
  const { params, ...init } = options
  let response: Response
  try {
    response = await fetchWithAuth(path, init, params)
  } catch (error) {
    logRequestError(path, init, error)
    throw error
  }

  if (response.status === 401) {
    response = await handleUnauthorized(
      path,
      () => fetchWithAuth(path, init, params),
      shouldRetry,
      response,
      generation,
      authToken,
    )
  }

  if (useAuthStore.getState().sessionGeneration !== generation) throw new ApiError(401, null)

  if (!response.ok) {
    const data = await response.json().catch(() => null)
    throw new ApiError(response.status, data)
  }

  return response
}

export const api = {
  get: <T>(path: string, options?: ApiRequestOptions) => request<T>(path, { ...options, method: 'GET' }),

  getStream: (path: string, options?: RequestInit) => requestStream(path, { ...options, method: 'GET' }),

  getBlob: (path: string, options?: BlobRequestOptions) => requestBlob(path, { method: 'GET', ...options }),

  postBlob: (path: string, body?: unknown, options?: RequestInit) =>
    requestBlob(path, {
      ...options,
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),

  post: <T>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    request<T>(path, {
      ...options,
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),

  postStream: (path: string, body?: unknown, options?: RequestInit & { params?: object | null }) =>
    requestStream(path, {
      ...options,
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),

  put: <T>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    request<T>(path, {
      ...options,
      method: 'PUT',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),

  patch: <T>(path: string, body?: unknown, options?: ApiRequestOptions) =>
    request<T>(path, {
      ...options,
      method: 'PATCH',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),

  delete: <T = void>(path: string, options?: ApiRequestOptions) => request<T>(path, { ...options, method: 'DELETE' }),
}

export { ApiError }
export default api
